import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldCheckIcon, ShieldSlashIcon, CheckCircleIcon,
} from "@phosphor-icons/react";

// Uses Supabase Auth's native TOTP MFA (enroll / challenge / verify /
// unenroll) directly — the old FastAPI-style /auth/2fa/* routes this page
// used to call don't exist on the live Supabase-backed app, so enrolling
// here previously failed silently. Native Supabase MFA has no backup-code
// concept, so that UI has been removed rather than left half-working.
export default function TwoFactorPage() {
  const [factor, setFactor] = useState(null);     // the verified TOTP factor, if any
  const [setupData, setSetupData] = useState(null); // { factorId, qr_code, secret }
  const [enterCode, setEnterCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) { toast.error(error.message); return; }
    setFactor(data?.totp?.find((f) => f.status === "verified") ?? null);
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true); setError("");
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
      if (error) throw error;
      setSetupData({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret, uri: data.totp.uri });
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: setupData.factorId });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: setupData.factorId,
        challengeId: challenge.id,
        code: enterCode.trim(),
      });
      if (verifyError) throw verifyError;
      setSetupData(null);
      setEnterCode("");
      await load();
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      setError(e.message); toast.error(e.message);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable two-factor authentication? Your account will only require a password after this.")) return;
    setBusy(true); setError("");
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
      if (error) throw error;
      await load();
      toast.success("Two-factor authentication disabled");
    } catch (e) {
      setError(e.message); toast.error(e.message);
    } finally { setBusy(false); }
  };

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4">
        <div className="trk-uppercase-label">Account</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0F172A]">Two-factor authentication</h1>
      </div>

      <div className="px-8 py-8 max-w-2xl trk-fade-in space-y-6">
        {/* Status card */}
        <div className="border border-border rounded-md p-6 bg-white" data-testid="2fa-status-card">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-md flex items-center justify-center ${factor ? "bg-[#DCFCE7]" : "bg-[#EFF6FF]"}`}>
              {factor
                ? <ShieldCheckIcon size={22} weight="fill" color="#059669" />
                : <ShieldSlashIcon size={22} weight="fill" color="#0176D3" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-medium text-[#0F172A]">
                  {factor ? "Two-factor is ON" : "Two-factor is OFF"}
                </div>
                {factor && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-[#DCFCE7] text-[#059669] border border-[#86EFAC]"
                        data-testid="2fa-badge-enabled">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#059669] inline-block" /> Enabled
                  </span>
                )}
              </div>
              <p className="text-sm text-[#475569] mt-1">
                {factor
                  ? "Signing in will ask for a 6-digit code from your authenticator app."
                  : "Add a one-time code from an authenticator app when signing in. Recommended for admins."}
              </p>
            </div>
          </div>
        </div>

        {/* Enable flow */}
        {!factor && !setupData && (
          <div className="border border-border rounded-md p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-[#0F172A]">Turn on two-factor</div>
                <p className="text-sm text-[#475569] mt-1">You'll need an authenticator app such as 1Password, Google Authenticator, Microsoft Authenticator, or Authy.</p>
              </div>
              <Button onClick={startSetup} disabled={busy} data-testid="2fa-start-setup-btn"
                      className="bg-[#0176D3] hover:bg-[#005FB2] text-white rounded-md">
                {busy ? "Loading…" : "Set up 2FA"}
              </Button>
            </div>
          </div>
        )}

        {!factor && setupData && (
          <form onSubmit={enable} className="border border-border rounded-md p-6 bg-white space-y-5 trk-fade-in" data-testid="2fa-setup-form">
            <div>
              <div className="font-medium text-[#0F172A] mb-1">1. Scan the QR code</div>
              <p className="text-xs text-[#475569] mb-3">Or enter the secret manually if your app doesn't scan.</p>
              <div className="flex items-start gap-4">
                <img src={setupData.qrCode} alt="2FA QR" className="w-40 h-40 border border-border rounded-md bg-white p-2" data-testid="2fa-qr-image" />
                <div className="flex-1 space-y-2">
                  <div>
                    <div className="trk-uppercase-label">Secret</div>
                    <div className="font-mono text-sm break-all bg-zinc-50 border border-border rounded-md px-2 py-1.5 mt-1" data-testid="2fa-secret">
                      {setupData.secret}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="font-medium text-[#0F172A] mb-1">2. Enter the 6-digit code your app shows</div>
              <Label className="trk-uppercase-label mb-2 block">Verification code</Label>
              <Input value={enterCode}
                     onChange={(e) => setEnterCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     inputMode="numeric" placeholder="123456"
                     data-testid="2fa-verify-code-input"
                     className="h-11 rounded-md text-xl font-mono tracking-widest text-center max-w-xs" />
            </div>

            {error && (
              <div className="text-sm rounded-md p-3" data-testid="2fa-setup-error"
                   style={{ color: "#B91C1C", background: "#FEE2E2", border: "1px solid #FCA5A5" }}>{error}</div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline"
                      onClick={async () => { await supabase.auth.mfa.unenroll({ factorId: setupData.factorId }); setSetupData(null); setEnterCode(""); setError(""); }}
                      data-testid="2fa-cancel-setup-btn" className="rounded-md">Cancel</Button>
              <Button type="submit" disabled={busy || enterCode.length !== 6} data-testid="2fa-enable-submit-btn"
                      className="bg-[#059669] hover:bg-[#047857] text-white rounded-md">
                <CheckCircleIcon size={16} weight="fill" className="mr-1.5" /> Enable 2FA
              </Button>
            </div>
          </form>
        )}

        {/* Disable flow */}
        {factor && (
          <div className="border border-border rounded-md p-6 bg-white space-y-4" data-testid="2fa-disable-form">
            <div>
              <div className="font-medium text-[#0F172A]">Turn off two-factor</div>
              <p className="text-sm text-[#475569] mt-1">This weakens your account security.</p>
            </div>
            {error && (
              <div className="text-sm rounded-md p-3" data-testid="2fa-disable-error"
                   style={{ color: "#B91C1C", background: "#FEE2E2", border: "1px solid #FCA5A5" }}>{error}</div>
            )}
            <div className="flex justify-end">
              <Button onClick={disable} disabled={busy} data-testid="2fa-disable-submit-btn"
                      className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md">
                Disable 2FA
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
